import { AnalyticsUserEvent } from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { useQueryClient } from '@tanstack/react-query';
import { Redirect, router } from 'expo-router';
import { atom, useAtom } from 'jotai';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Linking, Platform } from 'react-native';
import {
    type NavigationState,
    type Route,
    type SceneRendererProps,
    TabView,
} from 'react-native-tab-view';
import {
    ArrowRightIcon,
    BackButton,
    BackButtonIconType,
    BodyLargeRegular,
    ChecklistCompletionIndicator,
    FullScreenSpinner,
    GuideBlurb,
    H5Regular,
    H6Regular,
    Icon,
    isWeb,
    MemberTip,
    MessageCircleQuestionMarkIcon,
    PhoneIcon,
    SafeAreaView,
    SmoothieScrollView,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import useGetAllBankAccountDetailsFromGlobalState from '../../banking/hooks/useGetAllBankAccountDetailsFromGlobalState';
import {
    FRUITFUL_SUPPORT_PHONE_DISPLAY,
    FRUITFUL_SUPPORT_PHONE_URL,
} from '../../common/constants';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import {
    type BankAccountAndCard,
    getMoneyMapSpendRuleAccounts,
} from '../../common/utils/bankingSwitchUtil';
import { SIDEBAR_CONTENT_MAX_WIDTH } from '../../common/utils/homeUtils';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import { captureException, track } from '../../logger/Logger';
import { useMoneyMapWorkflow } from '../../roadmap/hooks/useMoneyMapWorkflow';
import useGetBankingAdoption from '../hooks/useGetBankingAdoption';
import { useUpdateBankingAdoption } from '../hooks/useUpdateBankingAdoption';
import {
    type BankingAdoption,
    BankingAdoptionTaskStatus,
    type BankingAdoptionUpdateDto,
} from '../types/BankingAdoption';
import AdoptionChecklistItem from './AdoptionChecklistItem';
import { BankingSwitchCompletedSheet } from './BankingSwitchCompletedSheet';

export const hasAlreadySeenCompletedSheetAtom = atom<boolean | undefined>(
    undefined,
);

export const BankingSwitch = ({
    layout,
}: {
    layout: 'widget' | 'full-screen';
}) => {
    const user = useAuthedUser();
    const toast = useToastController();
    const queryClient = useQueryClient();

    const {
        moneyMap,
        hasMoneyMapError,
        isWorkflowCompleted: isMoneyMapCompleted,
        isLoadingMoneyMap,
        isLoadingCompleteness,
    } = useMoneyMapWorkflow();
    const { data: bankingAdoption, isLoading: isLoadingBankingAdoption } =
        useGetBankingAdoption({
            enabled: isMoneyMapCompleted,
        });
    const { mutate: update } = useUpdateBankingAdoption();
    const { bankAccountDetails } = useGetAllBankAccountDetailsFromGlobalState();
    const isLoading =
        isLoadingBankingAdoption || isLoadingMoneyMap || isLoadingCompleteness;

    const [isDirectDepositChecked, setIsDirectDepositChecked] = useState(false);
    const [isBillSwitchChecked, setIsBillSwitchChecked] = useState(false);
    const [isAccountFundedChecked, setIsAccountFundedChecked] = useState(false);

    const {
        accountFundedStatus,
        billSwitchStatus,
        directDepositStatus,
        completedAt: adoptionCompletedAt,
    } = bankingAdoption ?? {};

    useEffect(() => {
        setIsDirectDepositChecked(
            directDepositStatus === BankingAdoptionTaskStatus.COMPLETE,
        );
    }, [directDepositStatus]);

    useEffect(() => {
        setIsBillSwitchChecked(
            billSwitchStatus === BankingAdoptionTaskStatus.COMPLETE,
        );
    }, [billSwitchStatus]);

    useEffect(() => {
        setIsAccountFundedChecked(
            accountFundedStatus === BankingAdoptionTaskStatus.COMPLETE,
        );
    }, [accountFundedStatus]);

    const updater = useCallback(
        (body: BankingAdoptionUpdateDto) => {
            update(
                {
                    body,
                },
                {
                    onSuccess: (newData: BankingAdoption) => {
                        void queryClient.setQueryData(
                            ['getBankingAdoption'],
                            newData,
                        );
                    },
                    onError: (error) => {
                        captureException(error, {
                            msg: 'Error updating banking adoption',
                            ...body,
                        });
                        toast.show(
                            'Something went wrong with updating that value. Please try again.',
                        );
                        void queryClient.invalidateQueries({
                            queryKey: ['getBankingAdoption'],
                        });
                    },
                },
            );
        },
        [update, queryClient],
    );

    const flip = useCallback((prevIsChecked: boolean) => {
        return prevIsChecked
            ? BankingAdoptionTaskStatus.INCOMPLETE
            : BankingAdoptionTaskStatus.COMPLETE;
    }, []);

    const updateDirectDeposit = useCallback(() => {
        setIsDirectDepositChecked((prev) => {
            updater({
                directDepositStatus: flip(prev),
            });
            track(AnalyticsUserEvent.BANKING_ADOPTION_ITEM_TOGGLED, {
                adoptionItem: 'directDeposit',
                adoptionItemMarkedComplete: !prev,
                adoptionListContext: layout,
            });
            return !prev;
        });
    }, [flip, updater]);

    const updateBillSwitch = useCallback(() => {
        setIsBillSwitchChecked((prev) => {
            updater({
                billSwitchStatus: flip(prev),
            });
            track(AnalyticsUserEvent.BANKING_ADOPTION_ITEM_TOGGLED, {
                adoptionItem: 'switchBills',
                adoptionItemMarkedComplete: !prev,
                adoptionListContext: layout,
            });
            return !prev;
        });
    }, [flip, updater]);

    const updateAccountFunded = useCallback(() => {
        setIsAccountFundedChecked((prev) => {
            updater({
                accountFundedStatus: flip(prev),
            });
            track(AnalyticsUserEvent.BANKING_ADOPTION_ITEM_TOGGLED, {
                adoptionItem: 'fundAccounts',
                adoptionItemMarkedComplete: !prev,
                adoptionListContext: layout,
            });
            return !prev;
        });
    }, [flip, updater]);

    const adoptionCompletedAtRef =
        useRef<BankingAdoption['completedAt']>(undefined);
    const [isCompletedSheetOpen, setIsCompletedSheetOpen] = useState(false);
    const [hasAlreadySeenCompletedSheet, setHasAlreadySeenCompletedSheet] =
        useAtom(hasAlreadySeenCompletedSheetAtom);

    useEffect(() => {
        const isAdoptionComplete = adoptionCompletedAt != null;

        // This 'if' condition should only be true until the first time we get the adoption data from the server
        if (adoptionCompletedAtRef.current === undefined) {
            // This should result in the BankingSwitch component rendering null or redirecting home
            setHasAlreadySeenCompletedSheet(isAdoptionComplete);
        } else {
            setIsCompletedSheetOpen(isAdoptionComplete);
        }

        adoptionCompletedAtRef.current = adoptionCompletedAt;
    }, [adoptionCompletedAt]);

    const onCompletedSheetClose = useCallback(() => {
        setHasAlreadySeenCompletedSheet(true);
        setIsCompletedSheetOpen(false);
    }, []);

    if (isLoading || hasAlreadySeenCompletedSheet === undefined) {
        if (layout === 'widget') {
            return null;
        }
        return <FullScreenSpinner />;
    }

    if (
        hasMoneyMapError ||
        !isMoneyMapCompleted ||
        bankingAdoption == null ||
        hasAlreadySeenCompletedSheet
    ) {
        if (layout === 'widget') {
            return null;
        }
        return <Redirect href="/home" />;
    }

    const spendRuleAccounts =
        moneyMap != null
            ? getMoneyMapSpendRuleAccounts(moneyMap, bankAccountDetails, user)
            : undefined;
    const spendBankAccountAndCard =
        spendRuleAccounts != null && spendRuleAccounts?.length > 0
            ? spendRuleAccounts[0]
            : undefined;

    const BankingSwitchContent =
        layout === 'widget' ? BankingSwitchWidget : BankingSwitchFullScreen;

    return (
        <BankingSwitchContent
            isDirectDepositChecked={isDirectDepositChecked}
            isBillSwitchChecked={isBillSwitchChecked}
            isAccountFundedChecked={isAccountFundedChecked}
            spendBankAccountAndCard={spendBankAccountAndCard}
            updateDirectDeposit={updateDirectDeposit}
            updateBillSwitch={updateBillSwitch}
            updateAccountFunded={updateAccountFunded}
            isCompletedSheetOpen={isCompletedSheetOpen}
            onCompletedSheetClose={onCompletedSheetClose}
        />
    );
};

interface BankingSwitchContentProps {
    isDirectDepositChecked: boolean;
    isBillSwitchChecked: boolean;
    isAccountFundedChecked: boolean;
    spendBankAccountAndCard: BankAccountAndCard | undefined;
    updateDirectDeposit: () => void;
    updateBillSwitch: () => void;
    updateAccountFunded: () => void;
    isCompletedSheetOpen: boolean;
    onCompletedSheetClose: () => void;
}

const BankingSwitchFullScreen = ({
    isDirectDepositChecked,
    isBillSwitchChecked,
    isAccountFundedChecked,
    spendBankAccountAndCard,
    updateDirectDeposit,
    updateBillSwitch,
    updateAccountFunded,
    isCompletedSheetOpen,
    onCompletedSheetClose,
}: BankingSwitchContentProps) => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('BankingSwitch/Checklist-FullScreen');

    return (
        <SafeAreaView edges={['top', 'left', 'right']}>
            <SmoothieScrollView>
                <YStack gap={12}>
                    <BackButton iconType={BackButtonIconType.ARROW_LEFT} />
                    <H5Regular paddingTop={24}>
                        Put your Money Map into action
                    </H5Regular>
                    <BodyLargeRegular
                        color="$gray1000"
                        paddingTop={8}
                        paddingBottom={24}
                    >
                        You&apos;re almost there — just a few steps to put your
                        plan into motion.
                    </BodyLargeRegular>
                    <DirectDepositCard
                        isMarkedComplete={isDirectDepositChecked}
                        onToggleComplete={updateDirectDeposit}
                    />
                    <SwitchBillsCard
                        isMarkedComplete={isBillSwitchChecked}
                        onToggleComplete={updateBillSwitch}
                    />
                    <FundAccountsCard
                        isMarkedComplete={isAccountFundedChecked}
                        onToggleComplete={updateAccountFunded}
                    />
                </YStack>
                <BankingSwitchCompletedSheet
                    spendBankAccountAndCard={spendBankAccountAndCard}
                    isOpen={isCompletedSheetOpen}
                    onClose={() => {
                        onCompletedSheetClose();
                        router.navigate('/home');
                    }}
                />
            </SmoothieScrollView>
        </SafeAreaView>
    );
};

const BankingSwitchWidget = ({
    isDirectDepositChecked,
    isBillSwitchChecked,
    isAccountFundedChecked,
    spendBankAccountAndCard,
    updateDirectDeposit,
    updateBillSwitch,
    updateAccountFunded,
    isCompletedSheetOpen,
    onCompletedSheetClose,
}: BankingSwitchContentProps) => {
    const user = useAuthedUser();
    const { value: goyoEnabled } = useFeatureGate(FeatureFlags.ENABLE_GOYO);
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('BankingSwitch/Checklist-HomeWidget');

    const [tabViewIndex, setTabViewIndex] = useState(
        !isDirectDepositChecked ? 0 : !isBillSwitchChecked ? 1 : 2,
    );

    const routes: Route[] = [
        {
            key: 'directDeposit',
            title: 'Direct deposit',
        },
        {
            key: 'switchBills',
            title: 'Switch bills',
        },
        {
            key: 'fundAccounts',
            title: 'Fund accounts',
        },
    ];

    const renderTabBar = useCallback(
        (
            _tabBarProps: SceneRendererProps & {
                navigationState: NavigationState<Route>;
            },
        ) => {
            return (
                <YStack
                    borderBottomWidth={1}
                    borderBottomColor="$gray600"
                    backgroundColor="$gray200"
                    paddingTop={16}
                    paddingHorizontal={16}
                    width="100%"
                >
                    <XStack
                        gap={12}
                        onPress={() => {
                            router.navigate('/banking-switch/checklist');
                        }}
                        cursor="pointer"
                        justifyContent="space-between"
                        width="100%"
                    >
                        <H6Regular>Put your Money Map into action</H6Regular>
                        <Icon icon={ArrowRightIcon} size={24} color="$black" />
                    </XStack>
                    <ChecklistCompletionIndicator
                        indicatorColor="$green1000"
                        totalStepCount={3}
                        completedSteps={[
                            isDirectDepositChecked,
                            isBillSwitchChecked,
                            isAccountFundedChecked,
                        ]}
                        completedOrdering="progress"
                    />
                </YStack>
            );
        },
        [isDirectDepositChecked, isBillSwitchChecked, isAccountFundedChecked],
    );

    const renderScene = useCallback(
        ({ route, jumpTo }: SceneRendererProps & { route: Route }) => {
            // Find the next screen to jump to
            switch (route.key) {
                case 'directDeposit':
                    return (
                        <DirectDepositCard
                            layout="widget"
                            isMarkedComplete={isDirectDepositChecked}
                            onToggleComplete={() => {
                                updateDirectDeposit();
                                if (!isBillSwitchChecked) {
                                    jumpTo('switchBills');
                                } else if (!isAccountFundedChecked) {
                                    jumpTo('fundAccounts');
                                }
                            }}
                        />
                    );
                case 'switchBills':
                    return (
                        <SwitchBillsCard
                            layout="widget"
                            isMarkedComplete={isBillSwitchChecked}
                            onToggleComplete={() => {
                                updateBillSwitch();
                                if (!isDirectDepositChecked) {
                                    jumpTo('directDeposit');
                                } else if (!isAccountFundedChecked) {
                                    jumpTo('fundAccounts');
                                }
                            }}
                        />
                    );
                case 'fundAccounts':
                    return (
                        <FundAccountsCard
                            layout="widget"
                            isMarkedComplete={isAccountFundedChecked}
                            onToggleComplete={() => {
                                updateAccountFunded();
                                if (!isBillSwitchChecked) {
                                    jumpTo('switchBills');
                                } else if (!isDirectDepositChecked) {
                                    jumpTo('directDeposit');
                                }
                            }}
                        />
                    );
                default:
                    return null;
            }
        },
        [isDirectDepositChecked, isBillSwitchChecked, isAccountFundedChecked],
    );

    const guideImageSrc = user.membership?.guide?.profileImageUrl;

    return (
        <YStack
            gap={16}
            width="100%"
            maxWidth={SIDEBAR_CONTENT_MAX_WIDTH}
            alignSelf="center"
        >
            {!goyoEnabled && (
                <GuideBlurb
                    guideImageSrc={guideImageSrc}
                    heading="Put your Money Map into action"
                    message="Here are the next steps to start automating your money system and enable automatic Income Split and bill coverage."
                />
            )}
            <View height={isWeb ? 'auto' : 240} width="100%">
                <TabView
                    style={{
                        backgroundColor: '$gray200',
                        width: '100%',
                        borderRadius: 16,
                    }}
                    onIndexChange={(index) => {
                        setTabViewIndex(index);
                    }}
                    navigationState={{
                        index: tabViewIndex,
                        routes,
                    }}
                    renderTabBar={renderTabBar}
                    renderScene={renderScene}
                    animationEnabled={true}
                />
                <BankingSwitchCompletedSheet
                    spendBankAccountAndCard={spendBankAccountAndCard}
                    isOpen={isCompletedSheetOpen}
                    onClose={onCompletedSheetClose}
                />
            </View>
            {!goyoEnabled && (
                <MemberTip
                    icon={MessageCircleQuestionMarkIcon}
                    text="Have questions about direct deposit or switching your bills? Reach out to our team with any questions!"
                    button={{
                        text: FRUITFUL_SUPPORT_PHONE_DISPLAY,
                        icon: PhoneIcon,
                        onPress:
                            Platform.OS === 'ios'
                                ? () => {
                                      void Linking.openURL(
                                          FRUITFUL_SUPPORT_PHONE_URL,
                                      );
                                  }
                                : undefined,
                    }}
                />
            )}
        </YStack>
    );
};

interface CtaCardProps {
    layout?: 'widget' | 'normal';
    isMarkedComplete: boolean;
    onToggleComplete: () => void;
}

const DirectDepositCard = ({
    layout = 'normal',
    isMarkedComplete,
    onToggleComplete,
}: CtaCardProps) => {
    return (
        <AdoptionChecklistItem
            layout={layout}
            title="Set up direct deposit"
            description="Connect your paycheck to Fruitful for automatic Income Split and bill coverage."
            isMarkedComplete={isMarkedComplete}
            onToggleComplete={onToggleComplete}
            ctaLabel="Set up direct deposit"
            onPressCta={() => {
                track(AnalyticsUserEvent.BANKING_ADOPTION_ITEM_CTA_PRESSED, {
                    adoptionItem: 'directDeposit',
                    adoptionListContext: layout,
                });
                router.push({
                    pathname: '/banking/direct-deposit/info',
                    params: {
                        context: 'home',
                        source: 'banking-adoption',
                    },
                });
            }}
        />
    );
};

const SwitchBillsCard = ({
    layout = 'normal',
    isMarkedComplete,
    onToggleComplete,
}: CtaCardProps) => {
    return (
        <AdoptionChecklistItem
            layout={layout}
            title="Switch bills"
            description="Update your bills to start paying them from your Fruitful account going forward."
            isMarkedComplete={isMarkedComplete}
            onToggleComplete={onToggleComplete}
            ctaLabel="Switch your bills"
            onPressCta={() => {
                track(AnalyticsUserEvent.BANKING_ADOPTION_ITEM_CTA_PRESSED, {
                    adoptionItem: 'switchBills',
                    adoptionListContext: layout,
                });
                router.push('/banking-switch/switch-bills');
            }}
        />
    );
};

const FundAccountsCard = ({
    layout = 'normal',
    isMarkedComplete,
    onToggleComplete,
}: CtaCardProps) => {
    return (
        <AdoptionChecklistItem
            layout={layout}
            title="Transfer funds"
            description="Transfer funds to Fruitful to make sure your upcoming bills are fully covered."
            isMarkedComplete={isMarkedComplete}
            onToggleComplete={onToggleComplete}
            ctaLabel="Fund Fruitful accounts"
            onPressCta={() => {
                track(AnalyticsUserEvent.BANKING_ADOPTION_ITEM_CTA_PRESSED, {
                    adoptionItem: 'fundAccounts',
                    adoptionListContext: layout,
                });
                router.push({
                    pathname: '/banking/transfer',
                    params: {
                        context:
                            layout === 'widget'
                                ? 'bankingAdoptionWidget'
                                : 'bankingAdoptionChecklist',
                    },
                });
            }}
        />
    );
};
